// 탭 순서 바꾸기(같은 창 탭 줄 안에서 끌기, 2026-09-13 사용자 요청) — 순수 계산만 모았다(DOM·React 없음).
//
// 끄는 동안 다른 탭이 **실시간으로 비켜 준다**(Chrome 식, 사용자 결정). 판정은 "지금 화면에 그려진" 이웃 탭의 가로 중심과
// 포인터를 비교한다 — 이웃의 중심을 넘으면 그 이웃과 자리를 바꾼다. 자리를 바꾼 뒤에는 이웃이 끄는 탭의 옛 자리로 가므로
// 되돌아가려면 **그 이웃의 새 중심**을 다시 넘어야 한다(탭 너비가 달라도 제자리에서 떨지 않는 이력 효과).

/**
 * 끄는 탭의 다음 미리보기 위치를 구한다.
 * @param centers 지금 화면에 그려진 순서대로 각 탭의 가로 중심(clientX, 끄는 탭 포함)
 * @param index 끄는 탭의 현재(미리보기) 위치
 * @param x 포인터의 clientX
 * @returns 새 위치(바뀌지 않으면 `index` 그대로)
 */
export function nextDragIndex(centers: readonly number[], index: number, x: number): number {
  let i = index;
  // 오른쪽 이웃들의 중심을 넘은 만큼 오른쪽으로(한 번의 이동으로 여러 칸을 건너뛸 수 있다 — 빠르게 끌 때).
  while (i + 1 < centers.length && x > centers[i + 1]) {
    i += 1;
  }
  if (i !== index) {
    return i;
  }
  while (i - 1 >= 0 && x < centers[i - 1]) {
    i -= 1;
  }
  return i;
}

/**
 * `from` 위치 항목을 `to` 위치로 옮긴 새 배열(원본 불변). `from` 이 범위 밖이면 원본 사본, `to` 는 범위 안으로 맞춘다.
 * @param to 옮긴 뒤의 위치(0 = 맨 앞)
 */
export function moveAt<T>(list: readonly T[], from: number, to: number): T[] {
  if (from < 0 || from >= list.length) {
    return [...list];
  }
  const rest = list.filter((_, i) => i !== from);
  const at = Math.max(0, Math.min(to, rest.length));
  return [...rest.slice(0, at), list[from], ...rest.slice(at)];
}

/** 목록에서 `id` 항목을 `to` 위치로 옮긴 새 배열([`moveAt`] 의 id 판) — 없는 id 면 원본 사본. */
export function moveById<T extends { id: string }>(list: readonly T[], id: string, to: number): T[] {
  return moveAt(
    list,
    list.findIndex((t) => t.id === id),
    to,
  );
}

/**
 * 미리보기 순서(id 목록)를 현재 탭 목록에 맞춘다 — 끄는 도중 탭이 닫히거나(없는 id 제거) 새로 열리면(뒤에 붙임)
 * 미리보기가 실제 목록과 어긋나지 않게 한다.
 */
export function reconcileOrder(order: readonly string[], ids: readonly string[]): string[] {
  const alive = new Set(ids);
  const kept = order.filter((id) => alive.has(id));
  const seen = new Set(kept);
  return [...kept, ...ids.filter((id) => !seen.has(id))];
}
